import { Storage } from '@aws-amplify/storage'
import {
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import { useAppState } from '../../../../appState'
import axios from '../../../../axios'
import { ENDPOINTS, ShoppingBoardView } from '../../../../constants'
import GenImageUploader from '../../../genImageUpload'

interface UserProfile {
  userName: string
  communityImage: string | null
  defaultShoppingBoardView: ShoppingBoardView
}

export interface CommunitySettingsModalProps extends DialogProps {
  username: string
  onClose: () => void
}

export default function CommunitySettingsModal({
  open,
  onClose,
  username,
}: CommunitySettingsModalProps) {
  const [appState] = useAppState() as any

  const userProfile = (appState?.userProfile || {}) as UserProfile
  const userId = appState?.userId || undefined

  const [image, setImage] = useState<string | null>(null)
  const [isUploadingImg, setIsUploadingImg] = useState(false)
  const [defaultView, setDefaultView] = useState(
    userProfile.defaultShoppingBoardView
  )

  useEffect(() => {
    ;(async () => {
      const response = await axios.get(ENDPOINTS.user.replace(':id', userId))
      setImage(response.data.data.communityImage)
    })()

    return () => {
      setImage(null)
      setIsUploadingImg(false)
    }
  }, [userId])

  const updateProfileField = async (
    field: 'communityImage' | 'defaultShoppingBoardView',
    value: string | ShoppingBoardView,
    successMessage = 'community image updated'
  ) => {
    try {
      await axios.patch(ENDPOINTS.user.replace(':id', userId), {
        [field]: value,
      })

      toast.success(successMessage)
    } catch (e) {
      toast.error('failed to update')
    }
  }

  const onUploadImage = async (files: any) => {
    try {
      setIsUploadingImg(true)

      if (files) {
        const image = files[0]
        if (!image) {
          throw new Error('image not supported')
        }

        const imageName = `comm-${uuidv4()}${image.name}`

        const result = await Storage.put(imageName, image, {
          level: 'public',
          contentType: image.type || 'image/gif, image/jpeg, image/png',
        })

        const imageUrl = `https://${process.env.GATSBY_S3_BUCKET_NAME}.s3.us-east-2.amazonaws.com/public/${result.key}`

        setImage(imageUrl)

        await updateProfileField('communityImage', imageUrl)
      }
    } catch (err) {
      toast.error('failed to update image')
    } finally {
      setIsUploadingImg(false)
    }
  }

  const onDeleteImage = async () => {
    try {
      setIsUploadingImg(true)

      setImage(null)

      await updateProfileField('communityImage', 'folder image deleted')
    } catch (err) {
      toast.error('failed to delete image')
    } finally {
      setIsUploadingImg(false)
    }
  }

  const handleDefaultViewChange = (view: ShoppingBoardView) => {
    setDefaultView(view)
    updateProfileField('defaultShoppingBoardView', view, 'default view updated')
  }

  const canEdit =
    !!userId && userProfile?.userName?.toLowerCase() === username?.toLowerCase()

  if (!canEdit) {
    return null
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="community-image-modal"
      maxWidth="lg"
      PaperProps={{
        style: { minWidth: '400px' },
      }}
    >
      <DialogTitle>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography variant="subtitle1">
            community profile settings
          </Typography>

          <IconButton size="small" aria-label="close-modal" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: '0.5rem 2rem 3rem 2rem' }}>
        <Box>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">
              default shopping board view
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={defaultView}
              onChange={e =>
                handleDefaultViewChange(e.target.value as ShoppingBoardView)
              }
            >
              <MenuItem value={ShoppingBoardView.ALL_ITEMS}>all items</MenuItem>
              <MenuItem value={ShoppingBoardView.ALL_FOLDERS}>
                all folders
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box mt={5}>
          <Typography variant="subtitle1">community image</Typography>

          <GenImageUploader
            imageSrc={image}
            label="community image"
            onUpload={onUploadImage}
            onDelete={onDeleteImage}
            isUploading={isUploadingImg}
            dialogState={open ? 'open' : 'closed'}
            showHeader={false}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
